// src/components/NoTagHomePage.js
import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useTypewriter, Cursor } from 'react-simple-typewriter';
import { Parallax } from 'react-parallax';
import { FaChartLine, FaLock, FaCogs, FaBars } from 'react-icons/fa';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import tmlogo from './images/tmlogo.png';
import spacebackground from './images/spacebackground.png';

const NoTagHomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [text] = useTypewriter({
    words: [
      'Accurate Metrics.',
      'Insightful Analytics.',
      'Empower Your Business.',
      'Grow Your Reviews.',
    ],
    loop: {},
    typeSpeed: 50,
    deleteSpeed: 30,
    delaySpeed: 2000,
  });

  const handleScrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Settings for react-slick
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
  };

  return (
    <Box sx={{ backgroundColor: '#0e0e0e', overflowX: 'hidden' }}>
      {/* Navigation Bar */}
      <AppBar position="fixed" sx={{ background: 'transparent', boxShadow: 'none' }}>
        <Toolbar sx={{ justifyContent: 'flex-end' }}>
          {!isMobile && (
            <Box>
              <Button color="inherit" onClick={() => handleScrollTo('features')}>
                Features
              </Button>
              <Button color="inherit" onClick={() => handleScrollTo('testimonials')}>
                Testimonials
              </Button>
              <Button color="inherit" component={Link} to="/dashboard">
                Dashboard
              </Button>
              <Button color="inherit" onClick={() => navigate('/contact')}>
                Contact
              </Button>
            </Box>
          )}
          {isMobile && (
            <IconButton edge="end" color="inherit" onClick={() => navigate('/menu')}>
              <FaBars size={24} />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Parallax
        bgImage={spacebackground}
        strength={500}
      >
        <Box
          sx={{
            minHeight: '100vh',
            position: 'relative',
            color: '#ffffff',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            paddingTop: '70px',
          }}
        >
          {/* Particle Effect */}
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'rgba(0,0,0,0.6)',
              zIndex: 1,
            }}
          >
            {/* You can integrate a particle effect library here */}
          </Box>

          <Container
            sx={{
              position: 'relative',
              zIndex: 2,
              marginTop: '0px',
            }}
          >
            {/* Logo */}
            <motion.img
              src={tmlogo}
              alt="TrueMetric Logo"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              style={{
                width: '200px',
                height: 'auto',
                marginBottom: '50px',
                filter: 'drop-shadow(0 0 0.25px rgba(255, 255, 255, 0.5))',
                cursor: 'pointer',
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />

            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  fontWeight: 'bold',
                  fontFamily: 'Montserrat, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '4px',
                  marginBottom: '20px',
                  color: '#ffffff',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                }}
              >
                Welcome to TrueMetric.io
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: '500',
                  fontFamily: 'Roboto, sans-serif',
                  marginBottom: '40px',
                  color: '#ffffff',
                  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                }}
              >
                <span>
                  {text}
                  <Cursor />
                </span>
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
              style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  padding: '15px 30px',
                  borderRadius: '30px',
                  fontSize: '1.2rem',
                  textTransform: 'none',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
                  background: 'linear-gradient(90deg, #FF8A00, #E52E71)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #E52E71, #FF8A00)',
                  },
                }}
                onClick={() => navigate('/contact')}
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  padding: '15px 30px',
                  borderRadius: '30px',
                  fontSize: '1.2rem',
                  textTransform: 'none',
                  color: '#ffffff',
                  borderColor: '#ffffff',
                  '&:hover': {
                    borderColor: '#FF8A00',
                    color: '#FF8A00',
                  },
                }}
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            </motion.div>
          </Container>
        </Box>
      </Parallax>

      {/* Features Section */}
      <Box
        id="features"
        sx={{
          padding: '80px 20px',
          backgroundColor: '#ffffff',
          color: '#0e0e0e',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            fontFamily: 'Montserrat, sans-serif',
            marginBottom: '40px',
          }}
        >
          Our Features
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '40px',
          }}
        >
          {/* Feature 1 */}
          <Box
            sx={{
              width: isMobile ? '100%' : '300px',
              textAlign: 'center',
            }}
          >
            <FaChartLine size={64} color="#FF8A00" />
            <Typography variant="h6" sx={{ marginTop: '20px', marginBottom: '10px' }}>
              Accurate Metrics
            </Typography>
            <Typography variant="body1">
              Get precise data to make informed business decisions.
            </Typography>
          </Box>

          {/* Feature 2 */}
          <Box
            sx={{
              width: isMobile ? '100%' : '300px',
              textAlign: 'center',
            }}
          >
            <FaLock size={64} color="#E52E71" />
            <Typography variant="h6" sx={{ marginTop: '20px', marginBottom: '10px' }}>
              Secure Platform
            </Typography>
            <Typography variant="body1">
              Your data is safe with our top-notch security measures.
            </Typography>
          </Box>

          {/* Feature 3 */}
          <Box
            sx={{
              width: isMobile ? '100%' : '300px',
              textAlign: 'center',
            }}
          >
            <FaCogs size={64} color="#8A2BE2" />
            <Typography variant="h6" sx={{ marginTop: '20px', marginBottom: '10px' }}>
              Customizable Tools
            </Typography>
            <Typography variant="body1">
              Tailor the platform to suit your business needs.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Testimonials Section */}
      <Box
        id="testimonials"
        sx={{
          padding: '80px 20px',
          backgroundColor: '#f5f5f5',
          color: '#0e0e0e',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            fontFamily: 'Montserrat, sans-serif',
            marginBottom: '40px',
          }}
        >
          What Our Clients Say
        </Typography>

        <Slider {...sliderSettings}>
          {/* Testimonial 1 */}
          <Box>
            <Typography variant="body1" sx={{ marginBottom: '20px' }}>
              "TrueMetric.io has revolutionized the way we handle analytics. Highly recommended!"
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              - Jane Doe, CEO of Acme Corp
            </Typography>
          </Box>

          {/* Testimonial 2 */}
          <Box>
            <Typography variant="body1" sx={{ marginBottom: '20px' }}>
              "An indispensable tool for our business growth."
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              - John Smith, Founder of Tech Solutions
            </Typography>
          </Box>

          {/* Add more testimonials as needed */}
        </Slider>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          padding: '20px',
          backgroundColor: '#0e0e0e',
          color: '#ffffff',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2">© {new Date().getFullYear()} TrueMetric.io. All rights reserved.</Typography>
      </Box>
    </Box>
  );
};

export default NoTagHomePage;
